import {
  isGuestWorkspaceRole,
  useCurrentAccount,
  useRoles,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  ColumnData,
  SlideAction,
  type SlidePanelRegistrar,
  useSlidePanel,
} from '@gripp/shared-ui';
import { startTransition, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useOutlet,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';
import { RoutineSubmitSource } from '../assetUtils';
import { AssetCard } from './assetCard';
import { AssetChatHeaderButton } from './assetChatHeaderButton';
import { AssetFilters } from './assetFilters';
import { StyleSheet, View } from 'react-native';

const ASSET_DETAIL_PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetDetailPanel.tsx';
const ASSET_CARD_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetCard.tsx';
const ASSET_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/routes.tsx';
const ASSET_DETAIL_ROUTE_PATTERN = '/assets/:assetId';
const ASSET_DETAIL_ROUTE_PANEL_KEY = 'asset-detail';
const ASSET_CARD_PANEL_DEBUG_ID = 'asset-card';

type AssetDetailLocationState = {
  from?: string;
};

const getLocationStateSource = (state: unknown) =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as AssetDetailLocationState).from
    : undefined;

const AssetDetailRightPanel = ({ column }: { column: ColumnData | null }) => {
  useSlidePanel('right', column, [column]);
  return null;
};

export const AssetDetailPanel = () => {
  const { assetId, routineId, workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const outletContext = useOutletContext<SlidePanelRegistrar | null>();
  const outlet = useOutlet(outletContext);
  const hasChildPanel = !!outlet;
  const source =
    new URLSearchParams(location.search).get('from') ??
    getLocationStateSource(location.state);
  const routineSubmitOwnsRightPanel =
    !!routineId && source === RoutineSubmitSource.RoutinesList;

  // ALLOWLIST, not deny-list (plan 0030 Phase 5): this panel is the OTHER
  // composition root for the ordinary asset page -- React Router matches
  // this nested `:assetId` route (not assetDetail.tsx's flat
  // `:assetId/activity/all` entry) for the bare `/assets/:assetId` URL a
  // guest lands on from their own history, so its header needs the same
  // gate as assetDetail.tsx's leftColumn.headerComponent. Fails closed:
  // unresolved account/roles/workspace data reads as guest.
  // `..` is not the list under every prefix this panel is mounted on: it
  // resolves against the route hierarchy, so from the layout route in
  // workspaces/routes.tsx it would land on `/`. `listPath` is `/assets` on the
  // assets prefix -- exactly what `..` resolved to there -- and `/workspaces`
  // under the workspace prefix.
  const { listPath } = useAssetRouting(assetId ?? '');
  const { workspaceAsset } = useWorkspaceAsset(assetId ?? '', !assetId);
  const { account, isLoading: isAccountLoading } = useCurrentAccount();
  const { roles, isLoading: isRolesLoading } = useRoles();
  const assetWorkspaceId = workspaceAsset?.workspace?.id;
  const isGuestOfAssetWorkspace =
    isAccountLoading || isRolesLoading || !assetWorkspaceId
      ? true
      : isGuestWorkspaceRole(assetWorkspaceId, account?.workspaceRoles, roles);

  const column = useMemo<ColumnData | null>(() => {
    if (!assetId) return null;
    return {
      title: 'assets.menu.title',
      mainComponent: <AssetCard assetId={assetId} />,
      // Off the allowlist for a guest -- edit/add-issue controls, not the
      // header or their own conversation.
      headerComponent: isGuestOfAssetWorkspace ? null : (
        <View style={styles.headerActionsRow}>
          <AssetChatHeaderButton
            assetId={assetId}
            routeWorkspaceId={workspaceId}
          />
          <AssetFilters assetId={assetId} />
        </View>
      ),
      showBackButton: true,
      ...(hasChildPanel
        ? {
            onBackButtonPress: () => startTransition(() => navigate('.')),
          }
        : {
            backButtonTransition: SlideAction.SINGLE,
            onBackButtonPress: () => startTransition(() => navigate(listPath)),
          }),
      debug: {
        id: ASSET_CARD_PANEL_DEBUG_ID,
        titleKey: 'assets.menu.title',
        component: 'AssetCard',
        componentSource: ASSET_CARD_SOURCE,
        columnSource: ASSET_DETAIL_PANEL_SOURCE,
        ownerComponent: 'AssetDetailPanel',
        ownerSource: ASSET_DETAIL_PANEL_SOURCE,
        routePanelKey: ASSET_DETAIL_ROUTE_PANEL_KEY,
        routePattern: ASSET_DETAIL_ROUTE_PATTERN,
        routeSource: ASSET_ROUTES_SOURCE,
        source: {
          owner: {
            file: ASSET_DETAIL_PANEL_SOURCE,
            line: 17,
            symbol: 'AssetDetailPanel',
          },
          column: {
            file: ASSET_DETAIL_PANEL_SOURCE,
            line: 21,
            symbol: 'column',
          },
          component: {
            file: ASSET_CARD_SOURCE,
            line: 30,
            symbol: 'AssetCard',
          },
          route: {
            file: ASSET_ROUTES_SOURCE,
            line: 67,
            symbol: 'assetsRoutes',
          },
        },
        entity: { type: 'asset', id: assetId },
      },
    };
  }, [
    assetId,
    hasChildPanel,
    listPath,
    navigate,
    isGuestOfAssetWorkspace,
    workspaceId,
  ]);

  return (
    <>
      {!routineSubmitOwnsRightPanel && (
        <AssetDetailRightPanel column={column} />
      )}
      {outlet}
    </>
  );
};

const styles = StyleSheet.create({
  headerActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
});
